import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Logo from '../components/Logo'
import { useAuth } from '../contexts/AuthContext'
import { acceptOrgInvitation, getOrgInvitePreview } from '../api'

type State = 'loading' | 'choice' | 'accepting' | 'success' | 'error'

interface InviteData {
  org_name: string
  role: string
  email: string
}

export default function InvitationAcceptPage() {
  const [params] = useSearchParams()
  const navigate   = useNavigate()
  const token      = params.get('token') || ''
  const { session, loading: authLoading } = useAuth()

  const [state, setState]     = useState<State>('loading')
  const [invite, setInvite]   = useState<InviteData | null>(null)
  const [errorMsg, setErrorMsg] = useState('')
  const acceptedRef = useRef(false)

  // Fetch invite preview — public, no auth required
  useEffect(() => {
    if (!token) { setState('error'); setErrorMsg('Missing invitation token.'); return }
    getOrgInvitePreview(token)
      .then(r => setInvite(r.data))
      .catch(e => {
        const status = e?.response?.status
        if (status === 404) setErrorMsg('This invitation link is no longer valid or has already been used.')
        else if (status === 409) setErrorMsg('This invitation has already been accepted.')
        else if (status === 410) setErrorMsg('This invitation has expired. Ask the org owner to send a new one.')
        else setErrorMsg(e?.response?.data?.detail || 'Something went wrong. Please try again.')
        setState('error')
      })
  }, [token])

  // Once we have the invite and know the auth state, decide what to show
  useEffect(() => {
    if (authLoading || !invite || state === 'error') return
    if (session) {
      doAccept()
    } else {
      setState('choice')
    }
  }, [authLoading, session, invite]) // eslint-disable-line react-hooks/exhaustive-deps

  const doAccept = async () => {
    if (acceptedRef.current) return
    acceptedRef.current = true
    setState('accepting')
    try {
      await acceptOrgInvitation(token)
      sessionStorage.removeItem('skilflow_pending_invite')
      setState('success')
    } catch (e: any) {
      acceptedRef.current = false
      const detail = e?.response?.data?.detail
      setErrorMsg(detail || 'Something went wrong. Please try again.')
      setState('error')
    }
  }

  const goSignup = () => {
    sessionStorage.setItem('skilflow_pending_invite', `org:${token}`)
    navigate(`/signup?redirect=${encodeURIComponent(`/invitations/accept?token=${token}`)}`)
  }

  const goLogin = () => {
    sessionStorage.setItem('skilflow_pending_invite', `org:${token}`)
    navigate(`/login?redirect=${encodeURIComponent(`/invitations/accept?token=${token}`)}`)
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ marginBottom: 32 }}>
        <Logo size="md" linkTo="/" />
      </div>

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '40px 36px', maxWidth: 420, width: '100%', textAlign: 'center', boxShadow: 'var(--shadow-lg)' }}>

        {(state === 'loading' || state === 'accepting') && (
          <>
            <div style={{ fontSize: 32, marginBottom: 16 }}>⏳</div>
            <div style={{ fontFamily: 'Sora, sans-serif', fontSize: 18, fontWeight: 700, color: 'var(--text1)', marginBottom: 8 }}>
              {state === 'accepting' ? 'Joining…' : 'Loading invitation…'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text3)' }}>Just a moment</div>
          </>
        )}

        {state === 'choice' && invite && (
          <>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: 'var(--brand-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--brand)" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            </div>
            <div style={{ fontFamily: 'Sora, sans-serif', fontSize: 20, fontWeight: 700, color: 'var(--text1)', marginBottom: 8 }}>You're invited!</div>
            <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 20, lineHeight: 1.6 }}>
              Join <strong style={{ color: 'var(--text1)' }}>{invite.org_name}</strong> as <strong style={{ color: 'var(--text1)' }}>{invite.role}</strong>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button onClick={goSignup} className="btn-primary" style={{ padding: '12px 32px', fontSize: 14, width: '100%' }}>
                Create account
              </button>
              <button onClick={goLogin} className="btn-ghost" style={{ padding: '10px 32px', fontSize: 14, width: '100%' }}>
                I already have an account — sign in
              </button>
            </div>
          </>
        )}

        {state === 'success' && invite && (
          <>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--success-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 24 }}>✓</div>
            <div style={{ fontFamily: 'Sora, sans-serif', fontSize: 20, fontWeight: 700, color: 'var(--text1)', marginBottom: 8 }}>
              Welcome to {invite.org_name}!
            </div>
            <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 28 }}>
              You've joined as <strong style={{ color: 'var(--text1)' }}>{invite.role}</strong>. You now have access to all projects assigned to this organization.
            </div>
            <button
              onClick={() => navigate('/')}
              style={{ padding: '10px 28px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', background: 'var(--brand)', color: '#000', cursor: 'pointer', fontFamily: 'inherit' }}>
              Go to dashboard →
            </button>
          </>
        )}

        {state === 'error' && (
          <>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(239,68,68,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 24 }}>✕</div>
            <div style={{ fontFamily: 'Sora, sans-serif', fontSize: 20, fontWeight: 700, color: 'var(--text1)', marginBottom: 8 }}>Invitation unavailable</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 28 }}>{errorMsg}</div>
            <button
              onClick={() => navigate('/')}
              style={{ padding: '10px 28px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text2)', cursor: 'pointer', fontFamily: 'inherit' }}>
              Back to dashboard
            </button>
          </>
        )}
      </div>

      <div style={{ marginTop: 24, fontSize: 11, color: 'var(--text3)', textAlign: 'center' }}>
        Powered by <span style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, color: '#1B3A5C' }}>mitel</span>
      </div>
    </div>
  )
}
